import { Container, Link } from "@chakra-ui/react"
import HeadingText from "@components/headings/HeadingText"
import NormalHeading from "@components/headings/NormalHeading"
import StylizedHeading from "@components/headings/StylizedHeading"
import HeaderLayout from "@components/layout/HeaderLayout"
import { EmenuPagesTranslation, OptionsProps } from "@definitions/types"
import { readItem } from "@directus/sdk"
import { routing } from "@i18n/routing"
import directusClient from "@utils/directus"
import { loadMessages } from "@utils/utils"
import i18nCountries from "i18n-iso-countries"
import { CountryCode } from "libphonenumber-js"
import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next"
import dynamic from "next/dynamic"
import NextLink from "next/link"
import { useTranslations } from "next-intl"
import { NextSeo } from "next-seo"
import { useEffect, useState } from "react"

const DynamicContactForm = dynamic(
  () => import("@components/form/ContactForm"),
  { ssr: false }
)
const DynamicFooter = dynamic(() => import("@components/layout/Footer"), {
  ssr: false,
})

const Contact: NextPage = ({
  countryData,
  page_data,
  locales,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const t = useTranslations("contact")
  const [country, setCountry] = useState<CountryCode | null>(null)

  useEffect(() => {
    const fetchCountry = async () => {
      const response = await fetch("/api/get-country")
      const data = await response.json()
      setCountry(data.country)
    }

    fetchCountry()
  }, [])

  return (
    <>
      <NextSeo
        title={page_data.meta_title}
        description={page_data.meta_description}
        canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/${page_data.slug}`}
        languageAlternates={locales.map((lang: string) => {
          const prefix = lang === routing.defaultLocale ? "" : `/${lang}`
          return {
            hrefLang: lang,
            href: `${process.env.NEXT_PUBLIC_SITE_URL}${prefix}/${page_data.slug}`,
          }
        })}
        additionalMetaTags={[
          {
            name: "keywords",
            content: page_data.meta_keywords,
          },
        ]}
      />
      <HeaderLayout />
      <Container
        maxW={"container.xl"}
        px={{ base: 10, xl: 0 }}
      >
        <NormalHeading as={"h1"}>{t("heading")}</NormalHeading>
        <StylizedHeading>{t("sub_heading")}</StylizedHeading>
        <HeadingText>
          {t("heading_text")}
          <Link
            as={NextLink}
            href={`mailto:info@digitalemenu.com`}
            borderBottomWidth={2}
            borderStyle={"dotted"}
            borderColor={"brand.primary"}
            _hover={{ textDecoration: "none", borderColor: "brand.secondary" }}
          >
            {`info@digitalemenu.com`}
          </Link>
        </HeadingText>
        {country && (
          <DynamicContactForm
            countryList={countryData}
            defaultCountry={country}
          />
        )}
      </Container>
      <DynamicFooter />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, locales }) => {
  // Country List
  const countries = i18nCountries.getNames(`${locale}`, { select: "official" })
  const countryData: OptionsProps[] = Object.keys(countries).map((key) => ({
    value: key,
    label: countries[key],
  }))

  // Page meta data
  const page_data = await directusClient
    .request(
      readItem("emenu_pages", 4, {
        fields: ["slug", { translations: ["*"] }],
      })
    )
    .then((res) => {
      const resultTranslations = res?.translations as EmenuPagesTranslation[]
      const translations = resultTranslations?.find(
        (t) => t.languages_id === locale
      )
      return {
        slug: res?.slug,
        meta_title: translations?.meta_title,
        meta_description: translations?.meta_description,
        meta_keywords: translations?.meta_keywords,
        title: translations?.title,
        content: translations?.content,
      }
    })

  const [shared, contact] = await Promise.all([
    loadMessages("shared", locale ?? routing.defaultLocale),
    loadMessages("contact", locale ?? routing.defaultLocale),
  ])

  return {
    props: {
      countryData,
      page_data,
      locales,
      messages: { ...shared, ...contact },
    },
  }
}

export default Contact
